import { useState, useEffect, useCallback } from 'react';
import { ANALYTICS_CONFIG, CookieConsent, DEFAULT_CONSENT } from '@/config/analytics';
import { supabase } from '@/integrations/supabase/client';

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Generate or retrieve visitor ID
  const getVisitorId = useCallback(() => {
    let visitorId = localStorage.getItem(ANALYTICS_CONFIG.VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem(ANALYTICS_CONFIG.VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  }, []);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(ANALYTICS_CONFIG.CONSENT_STORAGE_KEY + '_dismissed');
      const stored = localStorage.getItem(ANALYTICS_CONFIG.CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CookieConsent;
        const consentDate = new Date(parsed.consentedAt);
        const expiryMs = ANALYTICS_CONFIG.CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        const isExpired = Date.now() - consentDate.getTime() > expiryMs;

        if (parsed.consentVersion === ANALYTICS_CONFIG.CONSENT_VERSION && !isExpired) {
          setConsent(parsed);
          setShowBanner(false);
        } else {
          setShowBanner(!dismissed);
        }
      } else {
        setShowBanner(!dismissed);
      }
    } catch {
      setShowBanner(true);
    }
    setIsLoading(false);
  }, []);

  const dismissPermanently = useCallback(() => {
    localStorage.setItem(ANALYTICS_CONFIG.CONSENT_STORAGE_KEY + '_dismissed', '1');
    setShowBanner(false);
  }, []);

  // Save consent and log to database
  const saveConsent = useCallback(async (newConsent: Omit<CookieConsent, 'essential' | 'consentedAt' | 'consentVersion'>) => {
    const visitorId = getVisitorId();
    const fullConsent: CookieConsent = {
      essential: true,
      analytics: newConsent.analytics,
      marketing: newConsent.marketing,
      consentedAt: new Date().toISOString(),
      consentVersion: ANALYTICS_CONFIG.CONSENT_VERSION,
    };

    // Save to localStorage
    localStorage.setItem(ANALYTICS_CONFIG.CONSENT_STORAGE_KEY, JSON.stringify(fullConsent));
    setConsent(fullConsent);
    setShowBanner(false);

    // Consent stored locally only
    console.log('[CookieConsent] Consent saved:', fullConsent);
  }, [getVisitorId]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({ analytics: true, marketing: true });
  }, [saveConsent]);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(() => {
    saveConsent({ analytics: false, marketing: false });
  }, [saveConsent]);

  // Custom selection
  const saveCustom = useCallback((analytics: boolean, marketing: boolean) => {
    saveConsent({ analytics, marketing });
  }, [saveConsent]);

  // Check specific consent
  const hasAnalyticsConsent = useCallback(() => {
    return consent?.analytics ?? false;
  }, [consent]);

  const hasMarketingConsent = useCallback(() => {
    return consent?.marketing ?? false;
  }, [consent]);

  // Open settings (to change consent later)
  const openSettings = useCallback(() => {
    setShowBanner(true);
  }, []);

  return {
    consent,
    showBanner,
    isLoading,
    acceptAll,
    rejectNonEssential,
    saveCustom,
    hasAnalyticsConsent,
    hasMarketingConsent,
    openSettings,
    dismissPermanently,
    getVisitorId,
  };
}
